import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import {
    checkChildrenNeedChangeWidthHeight,
    getFrameAfterRotate,
} from '@src/slide/infrastructure/module/independent/graphic';
import { ICTTransform2D, DeltaOf } from '@tencent/slide-sdk-model';
import { angleToEmu, canvasToEmu, emuToAngle, emuToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import _set from 'lodash/set';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';

export interface IRelativeInfo {
    scaleX: number;
    scaleY: number;
    left: number;
    top: number;
}

export class FrameConverter {
    /**
     * 数据3.0转换接口
     */
    // eslint-disable-next-line complexity
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTransform2D>>,
        uiData: IFrame,
        relativeInfo: IRelativeInfo,
        isInGroup = false,
    ): void {
        let needRotate09Angle = false;

        let sdkRect: IRect = {
            left: sdkData.off?.x !== undefined ? sdkData.off?.x : 0,
            top: sdkData.off?.y !== undefined ? sdkData.off.y : 0,
            width: sdkData.ext?.cx !== undefined ? sdkData.ext.cx : 0,
            height: sdkData.ext?.cy !== undefined ? sdkData.ext.cy : 0,
        };

        if (sdkData.flipH !== undefined && sdkData.rot !== null) {
            uiData.flipH = sdkData.flipH ? 1 : 0;
        } else {
            delete uiData.flipH;
        }

        if (sdkData.flipV !== undefined && sdkData.rot !== null) {
            uiData.flipV = sdkData.flipV ? 1 : 0;
        } else {
            delete uiData.flipV;
        }

        if (sdkData.rot !== undefined && sdkData.rot !== null) {
            uiData.rotate = emuToAngle(sdkData.rot);
            if (isInGroup) {
                if (checkChildrenNeedChangeWidthHeight(
                    uiData.rotate | 0,
                    Number(uiData.flipH),
                    Number(uiData.flipV),
                )
                ) {
                    needRotate09Angle = true;
                }
            }
        } else {
            uiData.rotate = 0;
        }

        if (needRotate09Angle) {
            sdkRect = getFrameAfterRotate(sdkRect, 90);
        }

        if (sdkData.off?.x !== undefined) {
            uiData.left = (emuToCanvas(sdkRect.left) - relativeInfo.left) * relativeInfo.scaleX;
        }

        if (sdkData.off?.y !== undefined) {
            uiData.top = (emuToCanvas(sdkRect.top) - relativeInfo.top) * relativeInfo.scaleY;
        }

        if (sdkData.ext?.cx !== undefined) {
            uiData.width = emuToCanvas(sdkRect.width) * relativeInfo.scaleX;
        }

        if (sdkData.ext?.cy !== undefined) {
            uiData.height = emuToCanvas(sdkRect.height) * relativeInfo.scaleY;
        }

        if (needRotate09Angle) {
            const afterRotateRect: IRect = getFrameAfterRotate(uiData, -90);
            uiData.left = afterRotateRect.left;
            uiData.top = afterRotateRect.top;
            uiData.width = afterRotateRect.width;
            uiData.height = afterRotateRect.height;
        }
    }

    // eslint-disable-next-line complexity
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IFrame>,
        relativeInfo: IRelativeInfo,
        needRotate09Angle = false,
    ): DeltaOf<ICTTransform2D> {
        const xfrm: DeltaOf<ICTTransform2D> = {};

        if (uiData.flipH !== undefined) {
            xfrm.flipH = !!uiData.flipH;
        }
        if (uiData.flipV !== undefined) {
            xfrm.flipV = !!uiData.flipV;
        }
        if (uiData.rotate !== undefined) {
            xfrm.rot = angleToEmu(uiData.rotate);
        }
        if (needRotate09Angle) {
            if (uiData.left !== undefined || uiData.rotate !== undefined) {
                // @ts-ignore
                let rect: IRect = uiData;

                if (needRotate09Angle) {
                    rect = getFrameAfterRotate(uiData, 90);
                }
                rect = {
                    left: canvasToEmu(((rect.left || 0) / relativeInfo.scaleX) + relativeInfo.left),
                    top: canvasToEmu(((rect.top || 0) / relativeInfo.scaleY) + relativeInfo.top),
                    width: canvasToEmu(rect.width) / relativeInfo.scaleX,
                    height: canvasToEmu(rect.height) / relativeInfo.scaleY,
                };
                if (needRotate09Angle) {
                    rect = getFrameAfterRotate(rect, -90);
                }

                xfrm.off = {
                    x: Math.round(rect.left),
                    y: Math.round(rect.top),
                };
                xfrm.ext = {
                    cx: Math.round(rect.width),
                    cy: Math.round(rect.height),
                };
            }
        } else {
            if (uiData.left !== undefined) {
                _set(xfrm, 'off.x', Math.round(canvasToEmu(((uiData.left || 0) / relativeInfo.scaleX) + relativeInfo.left)));
            }
            if (uiData.top !== undefined) {
                _set(xfrm, 'off.y', Math.round(canvasToEmu(((uiData.top || 0) / relativeInfo.scaleY) + relativeInfo.top)));
            }
            if (uiData.width !== undefined) {
                _set(xfrm, 'ext.cx', Math.floor(canvasToEmu(uiData.width) / relativeInfo.scaleX));
            }
            if (uiData.height !== undefined) {
                _set(xfrm, 'ext.cy', Math.floor(canvasToEmu(uiData.height) / relativeInfo.scaleY));
            }
        }
        return xfrm;
    }
}
